import React, { useState, useEffect } from "react";
import { 
  UsersIcon,
  BuildingIcon,
  MapPinIcon,
  ActivityIcon,
  UserCheckIcon,
  HospitalIcon
} from "lucide-react";
import { mockData } from "../data/mock";
import { statsService } from "../services";

const StatsSection = () => {
  const [stats, setStats] = useState(mockData.stats);
  const [loading, setLoading] = useState(true);

  // Load statistics from API
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const dashboardStats = await statsService.getDashboardStats();
        
        // Transform API data to match component structure
        const transformedStats = [
          {
            icon: "users",
            number: dashboardStats.totalUsers || 0,
            label: "Happy Patients",
            description: "Patients treated successfully"
          },
          {
            icon: "user-check",
            number: dashboardStats.totalDoctors || 0,
            label: "Expert Doctors",
            description: "Qualified healthcare professionals"
          },
          {
            icon: "hospital",
            number: dashboardStats.totalClinics || 0,
            label: "Healthcare Centers",
            description: "Medical facilities across India"
          },
          {
            icon: "activity",
            number: dashboardStats.totalAppointments || 0,
            label: "Successful Treatments",
            description: "Completed medical procedures"
          }
        ];
        
        setStats(transformedStats);
        
      } catch (error) {
        console.error('Error loading statistics:', error);
        // Keep using mock data as fallback
        setStats(mockData.stats);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      "users": UsersIcon,
      "building": BuildingIcon,
      "map-pin": MapPinIcon,
      "activity": ActivityIcon,
      "user-check": UserCheckIcon,
      "hospital": HospitalIcon
    };
    return icons[iconName] || UsersIcon;
  };

  const getIconColor = (index) => {
    const colors = [
      "text-blue-500",
      "text-green-500", 
      "text-purple-500",
      "text-orange-500",
      "text-pink-500",
      "text-cyan-500"
    ];
    return colors[index % colors.length];
  };

  const getBgColor = (index) => {
    const colors = [
      "bg-blue-50",
      "bg-green-50", 
      "bg-purple-50",
      "bg-orange-50",
      "bg-pink-50",
      "bg-cyan-50"
    ];
    return colors[index % colors.length];
  };

  return (
    <section className="py-8 sm:py-12 lg:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Trusted by Millions
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4 sm:px-0">
            Leading healthcare provider with a proven track record of excellence
          </p>
        </div>

        {/* Stats Grid temporarily disabled */}
        {false && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gray-200 rounded-full mx-auto mb-3 animate-pulse"></div>
                <div className="h-8 bg-gray-200 rounded mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))
          ) : (
            stats.map((stat, index) => {
            const IconComponent = getIcon(stat.icon);
            return (
              <div key={stat.label} className="text-center group">
                <div className="flex flex-col items-center space-y-2 sm:space-y-3 lg:space-y-4">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 ${getBgColor(index)} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 ${getIconColor(index)}`} />
                  </div>
                  <div className="space-y-0.5 sm:space-y-1">
                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">{stat.number}</div>
                    <div className="text-xs sm:text-sm lg:text-base text-blue-600 font-medium">{stat.label}</div>
                  </div>
                </div>
              </div>
            );
          })
          )}
        </div>
        )}

        {/* Achievement Banner */}
        <div className="mt-12 sm:mt-14 lg:mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-center text-white">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">
              India's Leading Surgery Platform
            </h3>
            <p className="text-sm sm:text-base lg:text-lg xl:text-xl opacity-90 mb-6 sm:mb-8 max-w-3xl mx-auto px-4 sm:px-0">
              Revolutionizing healthcare with advanced surgical procedures, 
              experienced doctors, and patient-centric care across India
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">99%</div>
                <div className="text-xs sm:text-sm opacity-80">Success Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">24/7</div>
                <div className="text-xs sm:text-sm opacity-80">Support Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">100%</div>
                <div className="text-xs sm:text-sm opacity-80">All Insurance Covered</div>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Testimonial Preview temporarily disabled */}
        {false && (
        <div className="mt-12 sm:mt-14 lg:mt-16 bg-gray-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              What Our Patients Say
            </h3>
            <p className="text-sm sm:text-base text-gray-600">Real experiences from real patients</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Priya Sharma",
                treatment: "Laparoscopic Surgery",
                rating: 5,
                comment: "Excellent care and very professional staff. The surgery was smooth and recovery was faster than expected."
              },
              {
                name: "Rajesh Kumar",
                treatment: "Cataract Surgery",
                rating: 5,
                comment: "Outstanding service! Dr. Singh explained everything clearly. I can see perfectly now. Highly recommended."
              },
              {
                name: "Anita Verma", 
                treatment: "Hernia Surgery",
                rating: 5,
                comment: "Amazing experience from consultation to surgery. The care buddy system made everything so convenient."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-sm">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm sm:text-base">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-2 sm:ml-3">
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{testimonial.name}</div>
                    <div className="text-xs sm:text-sm text-gray-500">{testimonial.treatment}</div>
                  </div>
                </div>
                
                <div className="flex mb-2 sm:mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-base sm:text-lg">★</span>
                  ))}
                </div>
                
                <p className="text-gray-600 text-xs sm:text-sm italic leading-relaxed">
                  "{testimonial.comment}"
                </p>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

export default StatsSection;